import { AttendanceGuage, Button } from '@wot/components';
import { useClickOutside } from '@wot/shared';
import { ITrialClass } from '@wot/typings';
import formatInTimeZone from 'date-fns-tz/formatInTimeZone';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DEFAULT_PROFILE_PIC, MORE_SVG } from '../../assets/icons';
import { useAppSelector } from '../../hooks/useRedux';
import { LPlan } from '../../typings/plan';
import xhr from '../../utils/fetch';
import { capitalizeFirstLetter, getAttendacePercentage } from '../../utils/functions';

interface Props {
  plan: LPlan;
  userId?: string;
}

export function PlanCard({ plan, userId }: Props) {
  const [sessions, setSessions] = useState<ITrialClass[]>([]);
  const { timezone } = useAppSelector((state) => state.preferencesSlice);
  const navigate = useNavigate();
  const { profile } = useAppSelector((state) => state.profileSlice);
  const [viewMoreOpt, setViewMoreOpt] = useState(false);
  const viewMoreRef = useRef(null);
  useClickOutside(viewMoreRef, () => setViewMoreOpt(false));

  useEffect(() => {
    xhr({
      url: `/u/${profile?._id}/plans/${plan._id}/sessions`,
      method: 'GET'
    }).then((res) => {
      if (res.status === 'OK') {
        setSessions(res.data);
      }
    });
  }, []);

  function ViewMore() {
    return (
      <div
        ref={viewMoreRef}
        className="flex flex-col absolute top-12 right-5 bg-white drop-shadow-lg py-2 px-1 rounded-lg cursor-pointer"
      >
        {/* <span
          className="py-2 px-2 text-sm rounded-md hover:bg-neutralBlue2"
          onClick={() => {
            plan.changeRequest
              ? toast.error('You have already requested tutor change for this plan')
              : setChangeRequest(plan);
          }}
        >
          Request for Tutor Change
        </span> */}
        <span className="py-2 px-2 text-sm rounded-md hover:bg-neutralBlue2">Report an issue</span>
      </div>
    );
  }

  return (
    <div
      className={`w-full max-w-[1250px] relative flex flex-col md:flex-row gap-4 rounded-lg border-primary border-l-8 overflow-hidden border`}
    >
      <div className="w-full flex flex-col gap-7 px-4 md:px-7 py-4">
        <div>
          <div className="w-max bg-neutralBlue2 p-2 rounded-md">
            <p className="text-sm font-semibold">Plan ID: {plan._id}</p>
          </div>
          <div className="w-max py-1 flex items-center gap-2">
            <p className="text-sm text-neutralGray1">Plan Status: </p>
            <span className="rounded-full border text-xs font-medium px-3 py-1">{plan.status}</span>
          </div>
          <div className="w-max py-1 flex items-center gap-2">
            <p className="text-sm text-neutralGray1">Plan Category: </p>
            <span className="text-xs font-medium py-1">{capitalizeFirstLetter(plan.categoryType)}</span>
          </div>
          <div className="w-max py-1 flex items-center gap-2">
            <p className="text-sm text-neutralGray1">{`Plan ${plan.categoryType === 'subject' ? 'Subjects' : capitalizeFirstLetter(plan.categoryType)
              }:`}</p>
            <span className="text-xs font-medium py-1">{plan.category.name}</span>
          </div>
        </div>

        {plan.status !== 'cancelled' && (
          <div className="block md:hidden w-full mt-auto pb-4 border-b border-primary">
            <div className="w-full max-w-sm mx-auto flex gap-4 items-center justify-center">
              {/* TODO: Show cancel button only when the plan is active */}
              <Button
                block
                text="Cancel"
                type="s-outlined"
                onClick={() => {
                  if (userId) {
                    // setCancelPlan(plan);
                  } else {
                    // @ts-ignore
                    localStorage.setItem('prevPath', window.location.pathname);
                    navigate('/login');
                  }
                }}
              />
              {plan.status === 'active' && (
                <Button
                  block
                  text="Extend Plan"
                  onClick={() => {
                    if (userId) {
                      //   setExtendPlan(plan);
                    } else {
                      // @ts-ignore
                      localStorage.setItem('prevPath', window.location.pathname);
                      navigate('/login');
                    }
                  }}
                />
              )}
            </div>
          </div>
        )}

        <div className="w-full flex flex-col md:flex-row md:flex-nowrap gap-4 md:gap-7">
          <div className="flex flex-wrap gap-4 md:gap-7">
            <div className="flex flex-col gap-2">
              <p className="text-lg md:text-3xl font-bold text-primary">
                {sessions.filter((s) => s.status === 'completed').length}/{plan.totalSessions}
              </p>
              <p className="text-sm text-neutralGray2 font-medium">Completed Classes</p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-lg md:text-3xl font-bold text-primary">
                {plan.totalSessions - plan.purchasedSessions || 0}
              </p>
              <p className="text-sm text-neutralGray2 font-medium">Complimentary classes </p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-lg md:text-3xl font-bold text-primary">{plan.totalSessions - sessions.filter((s) => s.status === "completed" || s.status === "upcoming").length}</p>
              <p className="text-sm text-neutralGray2 font-medium">Remaining classes</p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-lg md:text-3xl font-bold text-primary">
                {sessions.filter((s) => s.status === 'upcoming').length}
              </p>
              <p className="text-sm text-neutralGray2 font-medium">Upcoming classes</p>
            </div>
            <div className="flex flex-wrap gap-7">
              <div className="flex flex-col gap-2">
                <p className="text-lg md:text-3xl font-bold text-primary">
                  {plan.tutorPayment || 0}
                  {/* To be fixed */}
                </p>
                <p className="text-sm text-neutralGray2 font-medium">Your Payment</p>
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-lg md:text-3xl font-bold text-primary">
                  {formatInTimeZone(plan.createdAt, timezone, 'd LLL y')}
                </p>
                <p className="text-sm text-neutralGray2 font-medium">Purchase Date</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-base font-semibold text-neutralGray2">ATTENDANCE</p>
            <div className="flex flex-row md:flex-col items-center gap-4">
              <div className="w-16 h-16 md:w-24 md:h-24 shrink-0">
                <AttendanceGuage
                  percentage={getAttendacePercentage(
                    sessions.filter((i) => i.attendance && i.attendance.student === 'present').length,
                    plan.totalSessions
                  )}
                />
              </div>
              <Link to={`/plans/${plan._id}/attendance`}>
                <p className="text-sm text-primary font-medium shrink-0 whitespace-nowrap underline underline-offset-1">
                  View detailed Attendance
                </p>
              </Link>
            </div>
          </div>
        </div>
      </div>
      {plan.user && (
        <div className="w-full md:w-max flex md:shrink-0">
          {/* <Link to={`/find-tutor/${plan.tutorId}`} className="w-full"> */}
          <div className="w-full h-full md:w-[300px] flex flex-col border border-neutralGray3 p-4 rounded-lg cursor-pointer bg-neutralBlue2">
            <div className="flex justify-end items-center gap-2">
              {/* {plan.tutor.level === 'premium' && <img src={PRO} alt="Pro badge" className="h-7 w-7" />} */}
              {/* {plan.status === 'active' && (
                <div className="w-full relative flex justify-end">
                  <img
                    src={MORE_SVG}
                    alt="More options"
                    className="h-5 w-5 cursor-pointer"
                    onClick={() => setViewMoreOpt(!viewMoreOpt)}
                  />
                  {viewMoreOpt && <ViewMore />}
                </div>
              )} */}
            </div>
            <div className="h-full flex flex-col gap-4 items-start md:items-center md:px-10 py-4">
              <div className="flex md:flex-col items-center gap-2">
                <div className="h-10 w-10 md:h-16 md:w-16 rounded-full overflow-hidden">
                  <img
                    src={plan.user.profilePic || DEFAULT_PROFILE_PIC}
                    alt="tutor_profile_pic"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-2xl font-normal">{plan.user.name}</span>
              </div>
              <div className="w-full max-w-sm mx-auto mt-auto flex flex-col gap-4 items-center justify-center whitespace-nowrap">
                <Button
                  onClick={() => navigate('/inbox', { state: { ...plan.user } })}
                  block
                  text="Message"
                  type="secondary"
                  size="sm"
                />
              </div>
            </div>
          </div>
          {/* </Link> */}
        </div>
      )}
    </div>
  );
}
